import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { Store } from '@ngrx/store';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { combineLatest } from 'rxjs';
import { generateRandomString } from '@utils/string-utils';
import { RestrictionRules } from './config';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopService } from '@app/services/shops/shop.service';
import * as CheckoutActions from '@app/pages/checkout-app/checkout-store/actions/checkout.actions';
import { ViewMode } from '../../../models';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Shippings } from '@pages/checkout-app/types';
import {
  BannerVariant,
  ComponentPlacement,
  DeliveryCountdownTimerFormGroup,
  ExpirationBehavior,
  ShippingMethodSelectionType,
  TimeUnit,
  TimerFormat,
} from './components/delivery-countdown-timer/models';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';

function timeThresholdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    const minThreshold = formGroup.get('minTimeThreshold')?.value;
    const minUnit = formGroup.get('minTimeUnit')?.value;
    const maxThreshold = formGroup.get('maxTimeThreshold')?.value;
    const maxUnit = formGroup.get('maxTimeUnit')?.value;

    if (minThreshold == null || maxThreshold == null || minUnit == null || maxUnit == null) {
      return null;
    }

    const minInMinutes = minUnit === TimeUnit.HOURS ? Number(minThreshold) * 60 : Number(minThreshold);
    const maxInMinutes = maxUnit === TimeUnit.HOURS ? Number(maxThreshold) * 60 : Number(maxThreshold);

    if (minInMinutes >= maxInMinutes) {
      return {
        timeThresholdMismatch: {
          message: 'Minimum time threshold cannot be greater than maximum time threshold',
        },
      };
    }

    return null;
  };
}

@Component({
  selector: 'delivery-countdown-timer',
  templateUrl: './delivery-countdown-timer.component.html',
  standalone: false,
})
export class DeliveryCountdownTimerComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;

  formGroup: FormGroup<DeliveryCountdownTimerFormGroup>;
  displayRules = [...RestrictionRules];
  displayRulesEntities;
  shopCurrencySymbol: string;
  shippingMethods: Array<{ id: number; name: string }> = [];

  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
    private extensionsService: ExtensionsService,
  ) {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.DELIVERY_COUNTDOWN_TIMER);
    this.extensionsService.save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleSave();
    });

    this.initializeComponent();
    this.subscribeToExtensionData();
  }

  private initializeComponent(): void {
    this.shopCurrencySymbol = this.shopService.shopCurrencySignal();
    this.displayRulesEntities = this.displayRules.map((r) => r.entity);
  }

  private subscribeToExtensionData(): void {
    this.store.dispatch(CheckoutActions.loadCheckouts());
    const shippingMethods$ = this.store.select(fromCheckoutReducer.selectShippingMethods);

    combineLatest([this.loadedExtension$, shippingMethods$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([extension, storeShippingMethods]: [ICheckoutExtensions, Shippings[]]) => {
        this.loadedExtension = extension;
        this.setShippingMethods(extension, storeShippingMethods);

        if (extension) {
          this.initForm(extension.template, extension.usageToken);
        } else {
          this.initForm();
        }
      });
  }

  private setShippingMethods(extension: ICheckoutExtensions, storeShippingMethods: Shippings[]): void {
    if ((extension as any)?.shippingMethods) {
      this.shippingMethods = (extension as any).shippingMethods;
    } else if (extension?.template?.shippingMethods) {
      this.shippingMethods = extension.template.shippingMethods;
    } else if (storeShippingMethods && storeShippingMethods.length > 0) {
      this.shippingMethods = storeShippingMethods
        .filter((method) => method.enabled && method.id)
        .map((method) => ({
          id: Number(method.id),
          name: method.shippingMethod?.displayName || method.name || 'Unknown',
        }));
    } else {
      this.shippingMethods = [];
    }
  }

  dispatchExampleViewValues(values: any) {
    if (!values) {
      return;
    }

    const template = {
      title: values.title,
      internalName: values.internalName,
      configurations: values.configurations,
      appearance: values.appearance,
    };

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.DELIVERY_COUNTDOWN_TIMER]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string) {
    // Map componentPlacement to placementMode for saving at root level
    const componentPlacement = loadedValues?.appearance?.componentPlacement;
    const placementMode =
      loadedValues?.placementMode ??
      (componentPlacement === ComponentPlacement.INSIDE_SHIPPING_METHOD ? 'linked' : 'standalone');

    this.formGroup = new FormGroup<DeliveryCountdownTimerFormGroup>({
      usageToken: new FormControl<string>(usageToken ?? 'delivery_countdown_timer_' + generateRandomString(12)),
      title: new FormControl<string>(loadedValues?.title ?? 'Delivery Countdown Timer', Validators.required),
      internalName: new FormControl<string>(loadedValues?.internalName ?? 'Delivery Countdown Timer'),
      placementMode: new FormControl<string>(placementMode),
      configurations: new FormGroup(
        {
          shippingMethodSelectionType: new FormControl<ShippingMethodSelectionType>(
            loadedValues?.configurations?.shippingMethodSelectionType ?? ShippingMethodSelectionType.FASTEST_AVAILABLE,
          ),
          selectedShippingMethodId: new FormControl<number | null>(
            loadedValues?.configurations?.selectedShippingMethodId ?? null,
          ),
          minTimeThreshold: new FormControl<number>(loadedValues?.configurations?.minTimeThreshold ?? 0),
          minTimeUnit: new FormControl<TimeUnit>(loadedValues?.configurations?.minTimeUnit ?? TimeUnit.MINUTES),
          maxTimeThreshold: new FormControl<number>(loadedValues?.configurations?.maxTimeThreshold ?? 24),
          maxTimeUnit: new FormControl<TimeUnit>(loadedValues?.configurations?.maxTimeUnit ?? TimeUnit.HOURS),
          expirationBehavior: new FormControl<ExpirationBehavior>(
            loadedValues?.configurations?.expirationBehavior ?? ExpirationBehavior.TIMER_DISAPPEARS,
          ),
          fallbackMessage: new FormControl<string>(
            loadedValues?.configurations?.fallbackMessage ?? 'Time for {shipping_method} has passed',
          ),
        },
        { validators: [timeThresholdValidator()] },
      ),
      appearance: new FormGroup({
        componentPlacement: new FormControl<ComponentPlacement>(
          loadedValues?.appearance?.componentPlacement ?? ComponentPlacement.STANDALONE,
        ),
        backgroundColor: new FormControl<BannerVariant>(
          (loadedValues?.appearance?.backgroundColor as BannerVariant) ?? 'info',
        ),
        displayText: new FormControl<string>(
          loadedValues?.appearance?.displayText ?? 'Order within {countdown} for delivery via {shipping_method}',
          Validators.required,
        ),
        timerFormat: new FormControl<TimerFormat>(loadedValues?.appearance?.timerFormat ?? TimerFormat.DIGITAL),
      }),
      displayRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.displayRules ?? [],
        this.displayRules,
      ) as FormArray<FormGroup<any>>,
    });

    if (loadedValues?.placementMode && !loadedValues?.appearance?.componentPlacement) {
      const componentPlacement =
        loadedValues.placementMode === 'linked'
          ? ComponentPlacement.INSIDE_SHIPPING_METHOD
          : ComponentPlacement.STANDALONE;
      this.formGroup.get('appearance.componentPlacement')?.setValue(componentPlacement, { emitEvent: false });
    }

    // Sync componentPlacement changes to placementMode (so it saves correctly at root level)
    this.formGroup
      .get('appearance.componentPlacement')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((componentPlacement) => {
        const placementMode =
          componentPlacement === ComponentPlacement.INSIDE_SHIPPING_METHOD ? 'linked' : 'standalone';
        this.formGroup.get('placementMode')?.setValue(placementMode, { emitEvent: false });
      });

    const initialComponentPlacement = this.formGroup.get('appearance.componentPlacement')?.value;
    const initialPlacementMode =
      initialComponentPlacement === ComponentPlacement.INSIDE_SHIPPING_METHOD ? 'linked' : 'standalone';
    this.formGroup.get('placementMode')?.setValue(initialPlacementMode, { emitEvent: false });

    this.dispatchExampleViewValues(this.formGroup.value);
    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });

    this.formGroup.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  handleSave() {
    const formValue = this.formGroup.value;
    const { usageToken, ...template } = formValue;
    const payload: ICheckoutExtensions = {
      template,
      usageToken,
      type: ExtensionComponentType.DELIVERY_COUNTDOWN_TIMER,
    };
    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
